import { isEmpty } from 'lodash';
import { type Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { type IAdvancedSelectColumns } from '../IAdvancedSelectColumns.ts';
import {
  formatValueByDatatype,
  type IFormatValueByDatatypeOptions,
} from './formatValueByDatatype.ts';

export interface IAdvancedSelectedOptionLabelValue {
  id: string;
  label: string;
  rawValue: string | null;
  type: Datatype;
}

/**
 * Estrae i valori delle colonne etichetta di un'opzione selezionata, filtrando
 * le colonne visibili (`labelFieldColumns` se presenti, altrimenti tutte) e
 * scartando i valori vuoti. Logica condivisa tra il rendering testuale e quello
 * a nodi React.
 */
export function getAdvancedSelectedOptionLabelValues(
  selectedValue: IWidgetAdvancedOption['values'] | null | undefined,
  labelFieldColumns?: string[],
  columns?: IAdvancedSelectColumns[],
  options?: IFormatValueByDatatypeOptions,
): IAdvancedSelectedOptionLabelValue[] {
  if (!selectedValue || isEmpty(selectedValue)) {
    return [];
  }

  const visibleColumns = labelFieldColumns?.length
    ? columns?.filter(c => labelFieldColumns.includes(c.id))
    : columns;

  if (!visibleColumns || visibleColumns.length === 0) {
    return [];
  }

  return visibleColumns
    .map(column => {
      const rawValue = selectedValue[column.id];
      const label = formatValueByDatatype(rawValue, column.type, options);
      if (!label) return null;

      return {
        id: column.id,
        label,
        rawValue,
        type: column.type,
      };
    })
    .filter(value => value != null);
}

/**
 * Etichetta TESTUALE dell'opzione selezionata: concatena le colonne etichetta
 * con " - ". Di default le colonne RichText vengono appiattite (HTML rimosso);
 * con `options.keepRichTextHtml` l'HTML viene mantenuto (es. idratazione PDF).
 * Usata dove serve una stringa piatta (riferimenti, label del dropdown) e
 * per l'idratazione PDF/export.
 */
export function renderAdvancedSelectedOptionLabel(
  selectedValue: IWidgetAdvancedOption['values'] | null | undefined,
  labelFieldColumns?: string[],
  columns?: IAdvancedSelectColumns[],
  options?: IFormatValueByDatatypeOptions,
): string {
  return getAdvancedSelectedOptionLabelValues(
    selectedValue,
    labelFieldColumns,
    columns,
    options,
  )
    .map(value => value.label)
    .join(' - ');
}
